import { UseCheckoutPricingInput } from '@recurly/react-recurly';
import { TCountries } from '~/constants/stateList';
import { TFuncKey } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { TCancellationReason } from '~/components/Membership/components/Cancellation/constants/cancellation-reasons';

export type TRecurlyAddress = Omit<
    UseCheckoutPricingInput['address'],
    'vat_number' | 'phone' | 'country'
> & {
    country: TCountries;
};

export type TRecurlyAddressFields = keyof TRecurlyAddress;

export type TRecurlyCreditCardFields = 'number' | 'month' | 'year' | 'cvv';

export type TRecurlyFields = TRecurlyAddressFields | TRecurlyCreditCardFields;

export type IBillingFormErrorsFields = Record<TRecurlyFields, string>;

export interface IBillingFormErrorsMeta {
    isValid: boolean;
    error: string;
}

export interface IBillingFormErrors {
    fields: IBillingFormErrorsFields;
    meta: IBillingFormErrorsMeta;
}

export type TBenefitKey = TFuncKey<
    typeof NAMESPACES.PRICING,
    'pricing_card.benefits'
>;

export interface IPricingPlanData {
    pricingTier: TActivePricingTier;
    pricingTierName: string;
    subtitleKey?: TFuncKey<typeof NAMESPACES.PRICING, 'pricing_card.subtitle'>;
    isPopular: boolean;
    crossedPrice?: string;
    benefitsIntroKey?: TFuncKey<
        typeof NAMESPACES.PRICING,
        'pricing_card.benefits_intro'
    >;
    planCode: TActivePlanCode;
    coupon?: string;
    period: TPricingPeriod;
    pricingTierAnalyticsProp: string;
}

export enum BillingModalType {
    Edit = 'edit',
    Create = 'create',
    Verify = 'verify',
    ChoosePlan = 'choosePlan',
    UpgradePay = 'upgradePay',
    UpgradeSuccess = 'upgradeSuccess',
    ChangeNextPlanSuccess = 'changeNextPlanSuccess'
}

export type TPlanCode = TLegacyPlanCode | TActivePlanCode;

export type TLegacyPlanCode =
    | 'starter_monthly'
    | 'unlimited_monthly'
    | 'unlimited_annual'
    | 'disable'
    | 'free'
    | 'lifetime'
    | 'monthly'
    // This is the plan you get from the cancellation flow - e.g. 5$ promo
    | 'monthly_basic'
    | 'annual';

export type TActivePlanCode =
    | 'starter_monthly_2023'
    | 'essentials_monthly'
    | 'premium_monthly'
    | 'starter_annual'
    | 'essentials_annual'
    | 'premium_annual';

export type TPricingPeriod = 'annual' | 'monthly';

export type TCancelChangeType = 'cancel_downgrade' | 'reactivate';

export type TActivePricingTier = 'starter' | 'essentials' | 'premium';
export type TPricingTier = TActivePricingTier | 'legacy';

export interface IBillingFieldError {
    field?: TRecurlyFields;
    messages?: string[];
}

export type TPollingType =
    | 'downgrade'
    | 'cancel_downgrade'
    | 'upgrade'
    | 'reactivate';

export enum InvoiceEnum {
    DueAt = 'due_at',
    PlanCode = 'plan_code',
    Total = 'total',
    Download = 'download'
}

interface IInvoiceLineItem {
    attributes: {
        plan_code: TPlanCode;
    };
}

export interface IInvoice {
    id: string;
    [InvoiceEnum.DueAt]: string;
    [InvoiceEnum.PlanCode]: TPlanCode;
    [InvoiceEnum.Total]: number;
    type: string;
    created_at: string;
    number: string;
    state: string;
    balance: number;
    tax: number;
    line_items: IInvoiceLineItem[];
    tax_info: ITaxInfo;
}

interface ITaxInfo {
    attributes: IAttributes;
}

interface IAttributes {
    type: string;
    region: string;
    rate: number;
}

export interface IInvoiceTableHeader {
    title: string;
    sortable: boolean;
    field: InvoiceEnum;
}

export interface IInvoiceHistoryResponse {
    invoices: IInvoice[];
    has_more: boolean;
}

export interface IBillingInfoResponse {
    account_id: string;
    first_name: string;
    last_name: string;
    address: Omit<TRecurlyAddress, 'first_name' | 'last_name'>;
    payment_method: {
        object: string;
        card_type: string;
        first_six: string;
        last_four: string;
        exp_month: number;
        exp_year: number;
    };
}

export interface IRecurlyTransformedAddress {
    city: string;
    country: TCountries;
    postal_code: string;
    region: string;
    street1: string;
    street2: string;
}

interface IRecurlyToken {
    token_id?: string;
}

export interface IRecurlyUpgradeRequest {
    plan_code: TPlanCode;
    coupon_code: string;
    unit_amount?: number;
}
export interface IRecurlyTokenRequest
    extends IRecurlyToken,
        IRecurlyUpgradeRequest {}

export interface IBillingInfoRequest extends IRecurlyToken {
    billing_info?: {
        address: IRecurlyTransformedAddress;
    };
}

export type IPlansAfterPriceUpdate = {
    full_amount: number;
    plan_name: string;
    discount?: number;
    amount_after_discount?: number;
    discount_type?: 'percentage' | 'fixed';
};

export type INextPaymentAfterPriceChangeResponse = {
    plans_after_price_update: IPlansAfterPriceUpdate[];
};

export interface IRecurlyPurchasePreviewResponse {
    total?: number;
    subtotal?: number;
    discount?: number;
    tax?: number;
    paid?: number;
    balance?: number;
    refundable_amount?: number;
    currency?: 'USD' | 'CAD';
    type?: 'charge' | 'credit' | 'legacy';
    collection_method?: 'automatic' | 'manual';
    origin?:
        | 'carryforward_credit'
        | 'carryforward_gift_credit'
        | 'credit'
        | 'external_refund'
        | 'gift_card'
        | 'immediate_change'
        | 'import'
        | 'line_item_refund'
        | 'open_amount_refund'
        | 'prepayment'
        | 'purchase'
        | 'refund'
        | 'renewal'
        | 'termination'
        | 'usage_correction'
        | 'write_off';
    tax_info?: {
        attributes: {
            type: string;
            region: string;
            rate: number;
            tax_details: Array<{
                type: string;
                region: string;
                rate: number;
                tax: number;
            }>;
        };
    };
}

export interface IRecurlySubscriptionChangeResponse {
    activate_at: string;
    activated: boolean;
    created_at: string;
    plan_code: TPlanCode;
    plan_name: string;
    subscription_id: string;
    unit_amount: number;
}

export type TCouponApplicablePricingPeriod = 'default' | TPricingPeriod;

export interface IBaseCancellationData {
    reason: TCancellationReason;
    feedback?: string;
    other_reason?: string;
}
export interface IPauseSubscriptionData
    extends Exclude<IBaseCancellationData, 'feedback'> {
    pauseDuration: number;
}

export type TUrgencyExperimentVariants = 'control' | 'urgent';

export type TMembershipBannerType =
    | 'cancelled'
    | 'paused'
    | 'downgraded'
    | 'awareness';
